import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileText, Plus, Edit, Trash2, Copy } from "lucide-react";
import PageHeader from "../../components/PageHeader";

const tiposPlantilla = ["general", "anticipo", "liquidacion", "contrato", "evento_proximo", "seguimiento_post", "resena", "proveedor", "personal"];

const variablesDisponibles = [
  "[nombre_cliente]", "[nombre_empresa]", "[fecha_evento]", "[hora_evento]",
  "[tipo_evento]", "[monto]", "[saldo]", "[anticipo]", "[fecha_limite]",
  "[nombre_evento]", "[responsable]",
];

const emptyForm = { nombre: "", tipo: "general", modulo: "", mensaje: "", activa: true };

const WhatsappPlantillas = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/plantillas`);
      setItems(r.data || []);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[WhatsappPlantillas] fetch falló:", err?.message); toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...emptyForm, ...t }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.nombre?.trim() || !form.mensaje?.trim()) {
      toast.error("Nombre y mensaje son requeridos");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/whatsapp/plantillas/${editing.template_id}`, form);
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/whatsapp/plantillas`, form);
        toast.success("Plantilla creada");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const handleDelete = async (t) => {
    if (t.es_sistema) {
      toast.error("Plantillas del sistema no se eliminan. Desactivala desde el editor.");
      return;
    }
    if (!window.confirm("Eliminar plantilla?")) return;
    try {
      await axios.delete(`${API}/whatsapp/plantillas/${t.template_id}`);
      toast.success("Eliminada");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const handleDuplicate = async (t) => {
    try {
      const { template_id: _id, created_at: _ca, updated_at: _ua, usuario_id: _uid, es_sistema: _es, ...rest } = t;
      void _id; void _ca; void _ua; void _uid; void _es;
      await axios.post(`${API}/whatsapp/plantillas`, { ...rest, nombre: `${t.nombre} (copia)`, es_sistema: false });
      toast.success("Duplicada");
      fetchData();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="plantillas-wa-page">
      <PageHeader
        title="Plantillas WhatsApp"
        description="Mensajes prearmados con variables como [nombre_cliente] o [saldo]. Se usan al crear recordatorios para enviar a clientes."
        primaryAction={{
          label: "Nueva plantilla",
          icon: Plus,
          testId: "btn-nueva-plantilla",
          onClick: openNew,
        }}
      />

      {loading ? (
        <div className="text-center py-8 text-stone-500">Cargando...</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-stone-300 mb-2" />
            <p className="text-stone-500">Sin plantillas. Crea la primera.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((t) => (
            <Card key={t.template_id} className={`border ${t.activa ? "border-stone-200" : "border-stone-200 opacity-60"}`} data-testid={`plantilla-${t.template_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-stone-900">{t.nombre}</h3>
                      {t.es_sistema && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800">Sistema</span>}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-700 capitalize">{t.tipo?.replace("_", " ")}</span>
                      {!t.activa && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-600">Inactiva</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-8 px-2"><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDuplicate(t)} className="h-8 px-2"><Copy className="w-4 h-4" /></Button>
                    {!t.es_sistema && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(t)} className="h-8 px-2"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-stone-600 whitespace-pre-wrap bg-stone-50 p-2 rounded font-mono text-xs">{t.mensaje}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-plantilla-wa">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar plantilla" : "Nueva plantilla WhatsApp"}</DialogTitle>
            <DialogDescription className="text-xs">Usa variables tipo [nombre_cliente] que se reemplazan al enviar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} disabled={editing?.es_sistema} placeholder="Ej: Recordatorio cumpleanos cliente" data-testid="input-tpl-nombre" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{tiposPlantilla.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modulo (opcional)</Label>
                <Input value={form.modulo || ""} onChange={(e) => setForm({ ...form, modulo: e.target.value })} placeholder="cliente / evento / pago / contrato / seguimiento" />
              </div>
              <div className="sm:col-span-2">
                <Label>Mensaje *</Label>
                <Textarea rows={6} value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} className="font-mono text-xs" data-testid="input-tpl-mensaje" />
                <p className="text-[10px] text-stone-500 mt-1">
                  Variables disponibles: {variablesDisponibles.join("  ")}
                </p>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between p-2 bg-stone-50 rounded">
                <Label className="cursor-pointer">Activa</Label>
                <Switch checked={form.activa} onCheckedChange={(c) => setForm({ ...form, activa: c })} data-testid="switch-tpl-activa" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11">Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-900 hover:bg-emerald-800 h-11 px-6" data-testid="btn-guardar-tpl">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsappPlantillas;
